import humanFileSize from "./humanFileSize.js";
import Instance from "./types/Instance.js";
import { addLog } from "./logger";
import { TypedArray } from "./types/ImageFrame";
import { cornerstone } from "./cornerstone/cornerstone-setup.js";
import * as instanceSource from "./cornerstone/instance-source";
import * as redactionStore from "./redaction/redaction-store";
import { assertFrameRedacted } from "./export/verify-redaction";
import { prepareAnonymizedDataset, removeAlphaChannel } from "./export/prepare-p10-dataset";

export default async function writeInstanceToBuffer(
	instance: Instance,
	// Reports per-frame progress while assembling the output pixel data, so a
	// caller's progress bar can move during a large multiframe write instead of
	// freezing on one instance. Called with (framesDone, framesTotal). May return
	// a promise (e.g. to yield a macrotask so the browser repaints) — it's awaited.
	onFrameProgress?: (done: number, total: number) => void | Promise<void>
) {
	// Finalize the header (transfer syntax, color tags, trim/NumberOfFrames, pixel
	// tag removed). Shared with the streaming writer so headers stay identical.
	const { dataset: anonymizedDicomData, isColor } = prepareAnonymizedDataset(instance);

	// Assemble the output pixel data one frame at a time. Loading every frame up
	// front (Promise.all) and, for color, mapping each through removeAlphaChannel,
	// held the whole instance's pixel data THREE times at once — the cached decoded
	// frames + the alpha-stripped copies + the concatenated buffer — which spiked a
	// large multiframe US to ~1.2 GB. Streaming keeps only the contiguous output
	// buffer plus one transient frame resident.
	//
	// All frames of a single SOP instance share dimensions, so the first frame
	// sizes the output buffer. (Branches on frames.length implicitly: a 1-frame
	// instance just runs the loop once.)
	let combined: Uint8Array | null = null;
	let offset = 0;
	const frameCount = instance.frames.length;

	// Pin the parsed source file for the whole instance. Without this each
	// loadImage below would re-read and re-parse the entire file, so a multiframe
	// cine would cost O(frames x fileSize) of I/O.
	const sourceUrl = instanceSource.urlFor(instance.imageId);
	const pinned = instanceSource.isFileBacked(instance.imageId);
	if (pinned) await instanceSource.acquire(sourceUrl, instance.imageId);
	try {
		for (let frameIndex = 0; frameIndex < frameCount; frameIndex++) {
			const frame = instance.frames[frameIndex];
			// Cache hit, or a decode from the source file. Either way the frame comes
			// back with the current redaction rectangles already applied.
			const img = await cornerstone.loadImage(frame.imageId);
			const px = img.imageFrame.pixelData as TypedArray;
			// For color, drop the canvas alpha channel (RGBA -> RGB). This is the only
			// per-frame allocation and it's released before the next iteration. For
			// non-color, view the bytes directly (no copy) — combined.set copies them in.
			const out = isColor
				? removeAlphaChannel(px)
				: new Uint8Array(px.buffer, px.byteOffset, px.byteLength);

			// Prove the committed rectangles are actually blank in the bytes we're
			// about to write. Throws (aborting the export) if they aren't — see
			// verify-redaction.ts for why this can't be left to the loaders alone.
			assertFrameRedacted(
				instance.imageId,
				frame.imageId,
				isColor ? out : px,
				img.imageFrame.rows,
				img.imageFrame.columns,
				redactionStore.fillValueFor(img)
			);

			if (!combined) {
				combined = new Uint8Array(out.byteLength * instance.frames.length);
			}
			combined.set(out, offset);
			offset += out.byteLength;

			// Free this decoded frame so the instance's frames don't all stay resident
			// while we serialize the P10 below. Unconditionally safe: any frame can be
			// decoded again from its source file.
			try {
				cornerstone.imageCache.removeImageLoadObject(frame.imageId);
			} catch {
				// already evicted / not in cache — fine
			}

			// Awaited so the caller can yield a macrotask between frames — frames pulled
			// from the cornerstone cache resolve on a microtask, which never lets the
			// browser repaint, so without this the bar freezes until the whole instance
			// is written.
			await onFrameProgress?.(frameIndex + 1, frameCount);
		}
	} finally {
		if (pinned) instanceSource.release(sourceUrl);
	}

	const pixelDataArrayBuffer = combined!.buffer as ArrayBuffer;

	if (instance.frames.length > 1) {
		addLog(
			"info",
			`${instance.imageId}: Writing multiframe P10 with ${instance.frames.length} frames`
		);
	}

	anonymizedDicomData.upsertTag("7FE00010", "OW", [pixelDataArrayBuffer]); // Set new pixel data
	instance.instanceData.dicomP10AnonymizedArrayBuffer = anonymizedDicomData.write({
		allowInvalidVRLength: true,
	});

	// Drop the pixel data we just injected. It's a full copy of this instance's
	// decoded frame bytes, only needed to serialize THIS P10 — the written buffer
	// above already contains it. Without this, every instance's anonymizedDicomData
	// (which stays on instance.instanceData as the reactive "is anonymized" flag)
	// keeps its pixel data alive, so across a large study they accumulate into a
	// second full copy of all pixel data (~the decoded study size) during the
	// write/upload phase. We delete only the tag (not the whole dataset) so the
	// header/UIDs and that flag remain. Re-running this function re-fetches frames
	// and re-upserts the tag, so removing it here is safe.
	delete anonymizedDicomData.dict["7FE00010"];

	addLog(
		"info",
		`${
			instance.imageId
		}: Generated new P10 buffer with decompressed pixel data, size: ${humanFileSize(
			instance.instanceData.dicomP10AnonymizedArrayBuffer.byteLength
		)}`
	);
	return instance;
}
